import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, map, catchError } from "rxjs/operators";
import { CashierScreenTypes } from "@nxplatform/platform-entities";
import { toggleCashierActiveWithdrawals } from "@nxplatform/store-shared/customer-cashier/shared-customer-cashier-actions";
import { CUSTOMER_CASHIER_FEATURE_KEY } from "@nxplatform/store-shared/customer-cashier/shared-customer-cashier-feature-keys";
import { __ } from "@service/api-client/http/helpers/translates";
var CashierMethods = /* @__PURE__ */ ((CashierMethods2) => {
  CashierMethods2["deposit"] = "deposit";
  CashierMethods2["withdrawal"] = "withdrawal";
  CashierMethods2["buyCrypto"] = "buyCrypto";
  return CashierMethods2;
})(CashierMethods || {});
var CashierTransactionStatuses = /* @__PURE__ */ ((CashierTransactionStatuses2) => {
  CashierTransactionStatuses2["processing"] = "processing";
  CashierTransactionStatuses2["successful"] = "successful";
  CashierTransactionStatuses2["failed"] = "failed";
  CashierTransactionStatuses2["pending"] = "pending";
  CashierTransactionStatuses2["complete"] = "complete";
  return CashierTransactionStatuses2;
})(CashierTransactionStatuses || {});
const initialCustomerCashierState = {
  method: "deposit",
  paymentMethods: [],
  categories: [],
  activePaymentMethod: null,
  activePaymentMethodGroup: null,
  session: null,
  isLoading: false,
  depositBonuses: [],
  depositBonusesLoading: false,
  depositBonusCode: void 0,
  depositBonusCodeSuccess: null,
  depositBonusCodeError: null,
  selectedDepositBonus: null,
  buyCrypto: [],
  requiredFields: null,
  error: null,
  showActiveWithdrawals: false,
  transactionInProgress: false,
  paymentFormReady: false,
  paymentFormFields: [],
  shouldSubmitTransactionForm: false,
  isAdditionalFormValid: true,
  isAdditionalFormDisabled: false,
  isAdditionalFormHidden: false,
  isConfirmationInitiated: false,
  cashierConfigV2: null,
  cashierConfigV2Loaded: false,
  transactionStatusFullScreen: false,
  lastSuccessfulPaymentMethods: null,
  lastSuccessfulPaymentMethodsLoaded: false,
  showLastSuccessfulPaymentMethods: false,
  cashierScreenType: CashierScreenTypes.base
};
const customerCashierSlice = createSlice({
  name: CUSTOMER_CASHIER_FEATURE_KEY,
  initialState: initialCustomerCashierState,
  reducers: {
    getPaymentMethodsStart: (state, action) => {
      state.isLoading = true;
    },
    getPaymentMethodsSuccess: (state, action) => {
      state.paymentMethods = action.payload.payment_methods;
      state.categories = action.payload.categories;
      state.isLoading = false;
    },
    getPaymentMethodsError: (state, action) => {
      state.isLoading = false;
    },
    getCashierSessionStart: (state) => {
      state.error = initialCustomerCashierState.error;
      state.isLoading = true;
    },
    getCashierSessionSuccess: (state, action) => {
      const { payment_methods, categories } = action.payload;
      state.session = action.payload;
      if (payment_methods && categories) {
        state.requiredFields = payment_methods.reduce((acc, curr) => {
          const { requiredFields } = curr || {};
          acc[curr.id] = requiredFields;
          return acc;
        }, {});
        state.paymentMethods = payment_methods;
        state.categories = categories;
        state.isLoading = false;
      }
    },
    updateCashierScreenType: (state, action) => {
      const { payload } = action;
      const { type } = payload;
      state.cashierScreenType = type;
    },
    getCashierSessionError: (state, action) => {
      var _a;
      state.error = (_a = action.payload) == null ? void 0 : _a.message;
      state.isLoading = false;
    },
    getDepositBonusesStart: (state) => {
      state.depositBonusesLoading = true;
    },
    getDepositBonusesSuccess: (state, action) => {
      const selected = state.depositBonusCode ? action.payload[0] : null;
      state.selectedDepositBonus = selected;
      state.depositBonuses = action.payload;
      state.depositBonusesLoading = false;
    },
    getDepositBonusesError: (state, action) => {
      state.depositBonusCode = initialCustomerCashierState.depositBonusCode;
      state.depositBonusesLoading = false;
    },
    setDepositBonusCode: (state, action) => {
      state.depositBonusCode = action.payload;
    },
    setDepositBonusCodeSuccess: (state, action) => {
      state.selectedDepositBonus = action.payload.depositBonus;
      state.depositBonusCodeSuccess = action.payload.message;
      state.depositBonusesLoading = false;
    },
    clearDepositBonusCodeSuccess: (state) => {
      state.depositBonusCodeSuccess = initialCustomerCashierState.depositBonusCodeSuccess;
    },
    setDepositBonusCodeError: (state, action) => {
      state.depositBonusCodeError = action.payload;
      state.depositBonusesLoading = false;
    },
    clearDepositBonus: (state) => {
      state.depositBonusCode = initialCustomerCashierState.depositBonusCode;
      state.selectedDepositBonus = initialCustomerCashierState.selectedDepositBonus;
    },
    setCashierMethod: (state, action) => {
      if (state.method !== action.payload) {
        state.method = action.payload;
        state.error = initialCustomerCashierState.error;
      }
    },
    setActivePaymentMethod: (state, action) => {
      state.activePaymentMethod = action.payload;
    },
    setActivePaymentMethodGroup: (state, action) => {
      state.activePaymentMethodGroup = action.payload;
    },
    clearActivePaymentMethod: (state) => {
      state.activePaymentMethod = initialCustomerCashierState.activePaymentMethod;
      state.activePaymentMethodGroup = initialCustomerCashierState.activePaymentMethodGroup;
    },
    resetDepositBonusState: (state, action) => {
      if (action.payload === false) {
        state.depositBonusCode = initialCustomerCashierState.depositBonusCode;
        state.selectedDepositBonus = initialCustomerCashierState.selectedDepositBonus;
      }
    },
    setDepositBonus: (state, action) => {
      state.selectedDepositBonus = action.payload;
    },
    resetCashierState: (state) => {
      state.method = initialCustomerCashierState.method;
      state.paymentMethods = initialCustomerCashierState.paymentMethods;
      state.activePaymentMethod = initialCustomerCashierState.activePaymentMethod;
      state.activePaymentMethodGroup = initialCustomerCashierState.activePaymentMethodGroup;
      state.buyCrypto = initialCustomerCashierState.buyCrypto;
      state.session = initialCustomerCashierState.session;
      state.isLoading = initialCustomerCashierState.isLoading;
      state.depositBonuses = initialCustomerCashierState.depositBonuses;
      state.depositBonusCode = initialCustomerCashierState.depositBonusCode;
      state.depositBonusesLoading = initialCustomerCashierState.depositBonusesLoading;
      state.selectedDepositBonus = initialCustomerCashierState.selectedDepositBonus;
      state.depositBonusCodeError = initialCustomerCashierState.depositBonusCodeError;
      state.error = initialCustomerCashierState.error;
      state.transactionInProgress = initialCustomerCashierState.transactionInProgress;
      state.paymentFormReady = initialCustomerCashierState.paymentFormReady;
      state.paymentFormFields = initialCustomerCashierState.paymentFormFields;
      state.isAdditionalFormValid = initialCustomerCashierState.isAdditionalFormValid;
      state.isAdditionalFormDisabled = initialCustomerCashierState.isAdditionalFormDisabled;
      state.isAdditionalFormHidden = initialCustomerCashierState.isAdditionalFormHidden;
      state.shouldSubmitTransactionForm = initialCustomerCashierState.shouldSubmitTransactionForm;
      state.isConfirmationInitiated = initialCustomerCashierState.isConfirmationInitiated;
      state.transactionStatusFullScreen = initialCustomerCashierState.transactionStatusFullScreen;
      state.showLastSuccessfulPaymentMethods = initialCustomerCashierState.showLastSuccessfulPaymentMethods;
    },
    getBuyCryptoGroupStart: (state) => {
    },
    getBuyCryptoGroupSuccess: (state, action) => {
      var _a, _b, _c;
      state.buyCrypto = (_c = (_b = (_a = action.payload) == null ? void 0 : _a.customTabs) == null ? void 0 : _b.buyCrypto) == null ? void 0 : _c.paymentMethods;
    },
    getBuyCryptoGroupError: (state, action) => {
      var _a;
      state.error = (_a = action.payload) == null ? void 0 : _a.message;
      state.isLoading = false;
    },
    getTransactionStart: (state) => {
      state.shouldSubmitTransactionForm = true;
      state.transactionInProgress = true;
    },
    getTransactionInProgress: () => {
    },
    setTransactionInProgress: (state, action) => {
      state.transactionInProgress = action.payload;
    },
    getTransactionFailed: (state) => {
      state.shouldSubmitTransactionForm = false;
      state.transactionInProgress = false;
      state.isAdditionalFormDisabled = false;
      state.isAdditionalFormHidden = false;
    },
    setPaymentFormReady: (state, action) => {
      state.paymentFormReady = action.payload;
    },
    setPaymentFormFields: (state, action) => {
      state.paymentFormFields = action.payload;
    },
    setAdditionalFormValid: (state, action) => {
      state.isAdditionalFormValid = action.payload;
    },
    setAdditionalFormDisabled: (state, action) => {
      state.isAdditionalFormDisabled = action.payload;
    },
    setAdditionalFormHidden: (state, action) => {
      state.isAdditionalFormHidden = action.payload;
    },
    setShouldSubmitTransactionForm: (state, action) => {
      state.shouldSubmitTransactionForm = action.payload;
    },
    setConfirmationInitiated: (state, action) => {
      state.isConfirmationInitiated = action.payload;
    },
    getCashierConfigV2Start: (state) => {
      state.cashierConfigV2Loaded = false;
    },
    getCashierConfigV2Success: (state, action) => {
      state.cashierConfigV2Loaded = true;
      state.cashierConfigV2 = action.payload;
    },
    getCashierConfigV2Failure: (state) => {
      state.cashierConfigV2Loaded = true;
    },
    setTransactionStatusFullScreen: (state, action) => {
      state.transactionStatusFullScreen = action.payload;
    },
    saveBonusSelectedStart: () => {
    },
    saveBonusSelectedSuccess: () => {
    },
    saveBonusSelectedFailure: () => {
    },
    resetCashierPaymentMethodSession: (state) => {
      state.transactionInProgress = initialCustomerCashierState.transactionInProgress;
      state.paymentFormReady = initialCustomerCashierState.paymentFormReady;
      state.paymentFormFields = initialCustomerCashierState.paymentFormFields;
      state.isAdditionalFormValid = initialCustomerCashierState.isAdditionalFormValid;
      state.isAdditionalFormDisabled = initialCustomerCashierState.isAdditionalFormDisabled;
      state.isAdditionalFormHidden = initialCustomerCashierState.isAdditionalFormHidden;
      state.shouldSubmitTransactionForm = initialCustomerCashierState.shouldSubmitTransactionForm;
      state.isConfirmationInitiated = initialCustomerCashierState.isConfirmationInitiated;
      state.transactionStatusFullScreen = initialCustomerCashierState.transactionStatusFullScreen;
      state.showLastSuccessfulPaymentMethods = initialCustomerCashierState.showLastSuccessfulPaymentMethods;
    },
    getLastSuccessfulPaymentMethodsStart: (state) => {
      state.lastSuccessfulPaymentMethodsLoaded = false;
    },
    getLastSuccessfulPaymentMethodsSuccess: (state, action) => {
      state.lastSuccessfulPaymentMethodsLoaded = true;
      state.lastSuccessfulPaymentMethods = action.payload;
    },
    getLastSuccessfulPaymentMethodsFailure: (state) => {
      state.lastSuccessfulPaymentMethodsLoaded = true;
    },
    setShowLastSuccessfulPaymentMethods: (state, action) => {
      state.showLastSuccessfulPaymentMethods = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(toggleCashierActiveWithdrawals, (state, action) => {
      state.showActiveWithdrawals = action.payload;
    });
  }
});
const customerCashierReducer = customerCashierSlice.reducer;
const {
  getPaymentMethodsStart,
  getPaymentMethodsSuccess,
  getPaymentMethodsError,
  getCashierSessionStart,
  getCashierSessionSuccess,
  getCashierSessionError,
  getDepositBonusesStart,
  getDepositBonusesSuccess,
  getDepositBonusesError,
  setDepositBonusCode,
  setDepositBonusCodeSuccess,
  clearDepositBonusCodeSuccess,
  setDepositBonusCodeError,
  clearDepositBonus,
  setCashierMethod,
  setActivePaymentMethod,
  setActivePaymentMethodGroup,
  clearActivePaymentMethod,
  resetDepositBonusState,
  setDepositBonus,
  resetCashierState,
  getBuyCryptoGroupStart,
  getBuyCryptoGroupSuccess,
  getBuyCryptoGroupError,
  getTransactionStart,
  getTransactionInProgress,
  setTransactionInProgress,
  getTransactionFailed,
  setPaymentFormReady,
  setPaymentFormFields,
  setAdditionalFormValid,
  setAdditionalFormDisabled,
  setAdditionalFormHidden,
  setShouldSubmitTransactionForm,
  setConfirmationInitiated,
  getCashierConfigV2Start,
  getCashierConfigV2Success,
  getCashierConfigV2Failure,
  setTransactionStatusFullScreen,
  saveBonusSelectedStart,
  saveBonusSelectedSuccess,
  saveBonusSelectedFailure,
  resetCashierPaymentMethodSession,
  getLastSuccessfulPaymentMethodsStart,
  getLastSuccessfulPaymentMethodsSuccess,
  getLastSuccessfulPaymentMethodsFailure,
  setShowLastSuccessfulPaymentMethods,
  updateCashierScreenType
} = customerCashierSlice.actions;
const getCustomerCashierState = (rootState) => rootState[CUSTOMER_CASHIER_FEATURE_KEY];
const selectCashierMethod = createSelector(
  getCustomerCashierState,
  (s) => s.method
);
const selectPaymentMethods = createSelector(
  getCustomerCashierState,
  (s) => {
    const isCrypto = s.method === "buyCrypto";
    const isWithdrawal = s.method === "withdrawal";
    if (isWithdrawal) {
      return s.paymentMethods;
    }
    if (isCrypto) {
      return s.paymentMethods.filter((method) => s.buyCrypto.includes(method.id));
    }
    return s.paymentMethods;
  }
);
const selectCashierScreenType = createSelector(
  getCustomerCashierState,
  (s) => s.cashierScreenType
);
const selectIsCryptoMethodsActive = createSelector(
  getCustomerCashierState,
  (s) => s.buyCrypto.length > 0
);
const selectCategories = createSelector(
  getCustomerCashierState,
  (s) => s.categories
);
const selectCashierSession = createSelector(
  getCustomerCashierState,
  (s) => s.session
);
const selectCashierSessionStartLink = createSelector(
  getCustomerCashierState,
  (state) => {
    var _a;
    return (_a = state.session) == null ? void 0 : _a.startLink;
  }
);
const selectRequiredFieldByPaymentId = createSelector(
  getCustomerCashierState,
  (s, paymentId) => paymentId,
  (s, paymentId) => (s.requiredFields || {})[paymentId] || null
);
const selectDepositBonuses = createSelector(
  getCustomerCashierState,
  (s) => s.depositBonuses
);
const selectDepositBonusesLoading = createSelector(
  getCustomerCashierState,
  (s) => s.depositBonusesLoading
);
const selectDepositBonusCodeSuccess = createSelector(
  getCustomerCashierState,
  (s) => s.depositBonusCodeSuccess
);
const selectDepositBonusCodeError = createSelector(
  getCustomerCashierState,
  (s) => s.depositBonusCodeError
);
const selectDepositBonusCode = createSelector(
  getCustomerCashierState,
  (s) => s.depositBonusCode
);
const selectActivePaymentMethod = createSelector(
  getCustomerCashierState,
  (s) => s.activePaymentMethod
);
const selectActivePaymentMethodGroup = createSelector(
  getCustomerCashierState,
  (s) => s.activePaymentMethodGroup
);
const selectCurrentDepositBonus = createSelector(
  getCustomerCashierState,
  (s) => s.selectedDepositBonus
);
const selectCashierError = createSelector(
  getCustomerCashierState,
  (s) => s.error
);
const selectCashierLoadingState = createSelector(
  getCustomerCashierState,
  (s) => s.isLoading
);
const isCashierActiveWithdrawalsOpen = createSelector(
  getCustomerCashierState,
  ({ showActiveWithdrawals }) => showActiveWithdrawals
);
const selectTransactionInProgress = createSelector(
  getCustomerCashierState,
  (s) => s.transactionInProgress
);
const selectPaymentFormReady = createSelector(
  getCustomerCashierState,
  (s) => s.paymentFormReady
);
const selectIsPgwPaymentFormValid = createSelector(
  getCustomerCashierState,
  (s) => {
    var _a;
    return !!((_a = s.paymentFormFields) == null ? void 0 : _a.length) && s.paymentFormFields.every((field) => field.isValid);
  }
);
const selectIsAdditionalFormValid = createSelector(
  getCustomerCashierState,
  (s) => s.isAdditionalFormValid
);
const selectIsAdditionalFormDisabled = createSelector(
  getCustomerCashierState,
  (s) => s.isAdditionalFormDisabled
);
const selectIsAdditionalFormHidden = createSelector(
  getCustomerCashierState,
  (s) => s.isAdditionalFormHidden
);
const selectShouldSubmitTransactionForm = createSelector(
  getCustomerCashierState,
  (s) => s.shouldSubmitTransactionForm
);
const selectConfirmationInitiated = createSelector(
  getCustomerCashierState,
  (s) => s.isConfirmationInitiated
);
const selectCashierConfigV2 = createSelector(
  getCustomerCashierState,
  (s) => {
    var _a;
    return ((_a = s.cashierConfigV2) == null ? void 0 : _a.enabled) ? s.cashierConfigV2 : null;
  }
);
const selectTransactionStatusFullScreen = createSelector(
  getCustomerCashierState,
  (s) => s.transactionStatusFullScreen
);
const selectLastSuccessfulPaymentMethods = createSelector(
  getCustomerCashierState,
  (s) => s.lastSuccessfulPaymentMethods
);
const selectLastSuccessfulPaymentMethodsLoaded = createSelector(
  getCustomerCashierState,
  (s) => s.lastSuccessfulPaymentMethodsLoaded
);
const selectShowLastSuccessfulPaymentMethods = createSelector(
  getCustomerCashierState,
  (s) => s.showLastSuccessfulPaymentMethods
);
const selectShowCashierV2 = createSelector(
  getCustomerCashierState,
  (s, countryCode) => countryCode,
  (s, countryCode) => {
    const { enabled, allowedCountries, restrictedCountries } = s.cashierConfigV2 || {};
    if (!enabled) {
      return false;
    }
    return (allowedCountries == null ? void 0 : allowedCountries.includes(countryCode)) || !(restrictedCountries == null ? void 0 : restrictedCountries.includes(countryCode));
  }
);
const selectShowCashierV2BonusPicker = createSelector(
  getCustomerCashierState,
  (s, countryCode) => countryCode,
  (s, countryCode) => {
    var _a;
    const { allowedCountries, restrictedCountries } = ((_a = s.cashierConfigV2) == null ? void 0 : _a.bonusPicker) || {};
    return (allowedCountries == null ? void 0 : allowedCountries.includes(countryCode)) || !(restrictedCountries == null ? void 0 : restrictedCountries.includes(countryCode));
  }
);
const fetchCashierSessionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCashierSessionStart.type),
  switchMap((action) => Api.cashier.createCashierSession(action.payload).pipe(
    map((data) => ({
      ...data,
      payment_methods: [...data.payment_methods].sort((a) => (a == null ? void 0 : a.isLatestSuccessfulMethod) ? -1 : 0)
    })),
    map((data) => getCashierSessionSuccess(data)),
    catchError((err) => {
      var _a, _b, _c;
      return of(
        getCashierSessionError(
          ((_a = err.data) == null ? void 0 : _a.data) || ((_b = err == null ? void 0 : err.response) == null ? void 0 : _b.data) || ((_c = err == null ? void 0 : err.response) == null ? void 0 : _c.message) || ""
        )
      );
    })
  ))
);
const fetchCashierSession = (data) => ({
  type: getCashierSessionStart.type,
  payload: data
});
const fetchDepositBonusesEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getDepositBonusesStart.type),
  switchMap(
    (action) => {
      var _a;
      return Api.cashier.getDepositBonuses((_a = action.payload) == null ? void 0 : _a.bonusCode).pipe(
        map((data) => {
          var _a2, _b, _c;
          if (action.payload) {
            if (data.items.length) {
              if ((_a2 = action.payload) == null ? void 0 : _a2.showTextMessages) {
                return setDepositBonusCodeSuccess({
                  depositBonus: data.items[0],
                  bonusCode: (_b = data.items[0]) == null ? void 0 : _b.bonusCode,
                  message: __("Cashier_._Bonus_._Bonus code activated!")
                });
              }
              return setDepositBonus(data.items[0]);
            }
            if ((_c = action.payload) == null ? void 0 : _c.showTextMessages) {
              return setDepositBonusCodeError(__("Cashier_._Bonus_._Bonus code does not exist"));
            }
            return getDepositBonusesError({
              data: {
                messages: __(
                  "Cashier_._Bonus code not found_._Wrong bonus code"
                ),
                bonusCode: action.payload
              }
            });
          }
          return getDepositBonusesSuccess(data == null ? void 0 : data.items);
        }),
        catchError((e) => of(getDepositBonusesError(e)))
      );
    }
  )
);
const fetchDepositBonuses = (bonusCode, showTextMessages) => ({
  type: getDepositBonusesStart.type,
  payload: bonusCode ? {
    bonusCode,
    showTextMessages
  } : void 0
});
const fetchCashierBuyCryptoEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBuyCryptoGroupStart.type),
  switchMap((action) => Api.cashier.getBuyCryptoGroup(action.payload).pipe(
    map((data) => getBuyCryptoGroupSuccess(data)),
    catchError((error) => of(getBuyCryptoGroupError(error)))
  ))
);
const fetchCashierBuyCrypto = (data) => ({
  type: getBuyCryptoGroupStart.type,
  payload: data
});
const fetchCashierConfigV2Epic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCashierConfigV2Start.type),
  switchMap(() => Api.cashier.getCashierConfigV2().pipe(
    map((data) => getCashierConfigV2Success(data)),
    catchError((error) => of(getCashierConfigV2Failure(error)))
  ))
);
const fetchCashierConfigV2 = () => ({
  type: getCashierConfigV2Start.type
});
const saveBonusSelected = (bonusParams) => ({
  type: saveBonusSelectedStart.type,
  payload: bonusParams
});
const saveBonusSelectedEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(saveBonusSelectedStart.type),
  switchMap((action) => Api.cashier.saveBonusSelected(action.payload).pipe(
    map(() => saveBonusSelectedSuccess()),
    catchError(() => of(saveBonusSelectedFailure()))
  ))
);
const getLastSuccessfulPaymentMethods = (params) => ({
  type: getLastSuccessfulPaymentMethodsStart.type,
  payload: params
});
const getLastSuccessfulPaymentMethodsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getLastSuccessfulPaymentMethodsStart.type),
  switchMap(
    (action) => Api.cashier.getLastSuccessfulPaymentMethods(action.payload).pipe(
      map((data) => getLastSuccessfulPaymentMethodsSuccess(data)),
      catchError(() => of(getLastSuccessfulPaymentMethodsFailure()))
    )
  )
);
export {
  CashierMethods,
  CashierTransactionStatuses,
  clearActivePaymentMethod,
  clearDepositBonus,
  clearDepositBonusCodeSuccess,
  customerCashierReducer,
  customerCashierSlice,
  fetchCashierBuyCrypto,
  fetchCashierBuyCryptoEpic,
  fetchCashierConfigV2,
  fetchCashierConfigV2Epic,
  fetchCashierSession,
  fetchCashierSessionEpic,
  fetchDepositBonuses,
  fetchDepositBonusesEpic,
  getBuyCryptoGroupError,
  getBuyCryptoGroupStart,
  getBuyCryptoGroupSuccess,
  getCashierConfigV2Failure,
  getCashierConfigV2Start,
  getCashierConfigV2Success,
  getCashierSessionError,
  getCashierSessionStart,
  getCashierSessionSuccess,
  getCustomerCashierState,
  getDepositBonusesError,
  getDepositBonusesStart,
  getDepositBonusesSuccess,
  getLastSuccessfulPaymentMethods,
  getLastSuccessfulPaymentMethodsEpic,
  getLastSuccessfulPaymentMethodsFailure,
  getLastSuccessfulPaymentMethodsStart,
  getLastSuccessfulPaymentMethodsSuccess,
  getPaymentMethodsError,
  getPaymentMethodsStart,
  getPaymentMethodsSuccess,
  getTransactionFailed,
  getTransactionInProgress,
  getTransactionStart,
  initialCustomerCashierState,
  isCashierActiveWithdrawalsOpen,
  resetCashierPaymentMethodSession,
  resetCashierState,
  resetDepositBonusState,
  saveBonusSelected,
  saveBonusSelectedEpic,
  saveBonusSelectedFailure,
  saveBonusSelectedStart,
  saveBonusSelectedSuccess,
  selectActivePaymentMethod,
  selectActivePaymentMethodGroup,
  selectCashierConfigV2,
  selectCashierError,
  selectCashierLoadingState,
  selectCashierMethod,
  selectCashierScreenType,
  selectCashierSession,
  selectCashierSessionStartLink,
  selectCategories,
  selectConfirmationInitiated,
  selectCurrentDepositBonus,
  selectDepositBonusCode,
  selectDepositBonusCodeError,
  selectDepositBonusCodeSuccess,
  selectDepositBonuses,
  selectDepositBonusesLoading,
  selectIsAdditionalFormDisabled,
  selectIsAdditionalFormHidden,
  selectIsAdditionalFormValid,
  selectIsCryptoMethodsActive,
  selectIsPgwPaymentFormValid,
  selectLastSuccessfulPaymentMethods,
  selectLastSuccessfulPaymentMethodsLoaded,
  selectPaymentFormReady,
  selectPaymentMethods,
  selectRequiredFieldByPaymentId,
  selectShouldSubmitTransactionForm,
  selectShowCashierV2,
  selectShowCashierV2BonusPicker,
  selectShowLastSuccessfulPaymentMethods,
  selectTransactionInProgress,
  selectTransactionStatusFullScreen,
  setActivePaymentMethod,
  setActivePaymentMethodGroup,
  setAdditionalFormDisabled,
  setAdditionalFormHidden,
  setAdditionalFormValid,
  setCashierMethod,
  setConfirmationInitiated,
  setDepositBonus,
  setDepositBonusCode,
  setDepositBonusCodeError,
  setDepositBonusCodeSuccess,
  setPaymentFormFields,
  setPaymentFormReady,
  setShouldSubmitTransactionForm,
  setShowLastSuccessfulPaymentMethods,
  setTransactionInProgress,
  setTransactionStatusFullScreen,
  updateCashierScreenType
};
